import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { api } from 'lib/axios'
import toast from 'react-hot-toast'

const initialState = {
  files: [],
  docs: [],
  tax_refund_form_files: [],
  filesLoading: false,
  filesLoader: false,
  deletingDocuments: {},
  error: null
}

export const deleteCustomerDocument = createAsyncThunk(
  'customerFiles/deleteDocument',
  async ({ customerId, docId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/Api/V8/module/Contacts/${customerId}/relationships/documents/${docId}`
      )
      return { docId }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to delete document'
      )
    }
  }
)

export const customerFilesSlice = createSlice({
  name: 'customerFiles',
  initialState,
  reducers: {
    initializeCustomerFiles: state => {
      return initialState
    },
    resetCustomerFiles: () => initialState,

    addCustomerFile: state => {
      state.files.push({})
    },
    addCustomerFileWithData: (state, action) => {
      state.files.push({
        file_obj: action.payload.file_obj,
        base64: action.payload.base64
      })
    },
    setCustomerFile: (state, action) => {
      const { file_no, file_obj, base64 } = action.payload
      state.files[file_no] = {
        file_obj,
        base64
      }
    },
    setCustomerFileID: (state, action) => {
      const { file_name, id, document_revision_id } = action.payload
      const file = {
        file_name,
        id,
        file_obj: { document_revision_id }
      }
      state.files.unshift(file)
    },
    removeFile: (state, action) => {
      const { file_no } = action.payload
      state.files.splice(file_no, 1)
    },

    // Bulk file operations
    setAllCustomerFiles: (state, action) => {
      state.files = action.payload.files
    },
    setAllDocs: (state, action) => {
      state.docs = action.payload.files
    },
    setDocFiles: (state, action) => {
      state.files = action.payload.data
    },
    // setFilterFiles: (state, action) => {
    //   state?.files?.push({ ...action.payload.file })
    // },

    setFilterFiles: (state, action) => {
      const incomingFile = action.payload.file
      const incomingId =
        incomingFile?.file_obj?.id ||
        incomingFile?.id ||
        incomingFile?.file_obj?.document_revision_id

      const alreadyExists = state?.files?.some(existing => {
        const existingId =
          existing?.file_obj?.id ||
          existing?.id ||
          existing?.file_obj?.document_revision_id
        return incomingId && existingId && existingId === incomingId
      })

      if (!alreadyExists) {
        const resolvedFileName =
          incomingFile?.fileName ||
          incomingFile?.file_obj?.name ||
          incomingFile?.file_obj?.file_name ||
          ''

        state.files.push({
          ...incomingFile,
          fileName: resolvedFileName
        })
      }
    },

    // Document operations
    setSingleDoc: (state, action) => {
      state.docs.push({ ...action.payload.file })
    },
    removeUploadedDoc: (state, action) => {
      const { id } = action.payload
      state.files = state.files.filter(file => {
        const fileId = file?.file_obj?.id
        const revId = file?.file_obj?.document_revision_id
        return fileId !== id && revId !== id
      })
    },
    setUpdatedDoc: (state, action) => {
      const { id, data } = action.payload
      const fileIndex = state?.files?.findIndex(file => file.id === id)
      if (fileIndex !== -1) {
        state.files[fileIndex] = { ...state.files[fileIndex], ...data }
      }
    },

    addCustomerRefundFormFile: state => {
      state.tax_refund_form_files.push({})
    },
    addCustomerRefundFormFileWithData: (state, action) => {
      state.tax_refund_form_files.push({
        file_obj: action.payload.file_obj,
        base64: action.payload.base64
      })
    },
    setAllCustomerRefundFormFiles: (state, action) => {
      state.tax_refund_form_files = action.payload.files
    },
    setCustomerRefundFormFile: (state, action) => {
      const { file_no, file_obj, base64 } = action.payload
      state.tax_refund_form_files[file_no] = {
        file_obj,
        base64
      }
    },
    setCustomerRefundFormFileID: (state, action) => {
      const { file_no, id, document_revision_id } = action.payload
      state.tax_refund_form_files[file_no].id = id
      if (document_revision_id) {
        state.tax_refund_form_files[file_no].file_obj.document_revision_id =
          document_revision_id
        state.tax_refund_form_files[file_no].file_obj.id = id
      }
    },
    removeCustomerRefundFormFile: (state, action) => {
      const { file_no } = action.payload
      state.tax_refund_form_files.splice(file_no, 1)
    },

    // Loading states
    setFilesLoading: (state, action) => {
      state.filesLoading = action.payload
    },
    setFilesLoader: (state, action) => {
      state.filesLoader = action.payload
    },
    setAllFilesLoader: (state, action) => {
      state.filesLoader = action.payload.loader
    },
    setAllFilesLoading: (state, action) => {
      state.filesLoading = action.payload.loader
    },

    // Progress and error handling
    updateFileProgress: (state, action) => {
      const { id, progress, status } = action.payload
      const file = state.files.find(f => f.id === id)
      if (file) {
        file.progress = progress
        file.status = status
      }
    },
    setFileError: (state, action) => {
      const { id, isError } = action.payload
      const file = state.files.find(f => f.id === id)
      if (file) {
        file.isError = isError
      }
    },

    // Individual document deletion loading states
    setDocumentDeleting: (state, action) => {
      const { docId, isDeleting } = action.payload
      if (isDeleting) {
        state.deletingDocuments[docId] = true
      } else {
        delete state.deletingDocuments[docId]
      }
    },
    setDocumentsFromAPI: (state, action) => {
      state.files = action.payload
    },
    sanitizeLibraryFileById: (state, action) => {
      const { fileId } = action.payload || {}
      if (!fileId || !Array.isArray(state.files)) return
      state.files.forEach((f, i) => {
        if (!f) return
        const matchId =
          f?.file_obj?.id ||
          f?.file_obj?.document_revision_id ||
          f?.id
        if (matchId && matchId === fileId) {
          state.files[i] = {
            ...f,
            ...(f?.gb_doc_type !== undefined
              ? { gb_doc_type: null }
              : {}),
            ...(f?.new_relation_addition !== undefined
              ? { new_relation_addition: false }
              : {}),
            file_obj: f?.file_obj
              ? {
                ...f.file_obj,
                gb_doc_type: null,
                new_relation_addition: false
              }
              : f?.file_obj
          }
        }
      })
    }
  },
  extraReducers: builder => {
    builder
      .addCase(deleteCustomerDocument.pending, (state, action) => {
        // Set individual document as deleting
        const { docId } = action.meta.arg
        state.deletingDocuments[docId] = true
      })
      .addCase(deleteCustomerDocument.fulfilled, (state, action) => {
        // Remove individual document from deleting state
        const { docId } = action.payload
        delete state.deletingDocuments[docId]
        state.files = state.files.filter(file => file.file_obj?.id !== docId)
        toast.success('Document deleted successfully')
      })
      .addCase(deleteCustomerDocument.rejected, (state, action) => {
        // Remove individual document from deleting state on error
        const { docId } = action.meta.arg
        delete state.deletingDocuments[docId]
        state.error = action.payload
        toast.error('Failed to delete document')
      })
  }
})

export const {
  // Initialize and reset
  initializeCustomerFiles,
  resetCustomerFiles,

  // File management
  addCustomerFile,
  addCustomerFileWithData,
  setCustomerFile,
  setCustomerFileID,
  removeFile,

  // Bulk operations
  setAllCustomerFiles,
  setAllDocs,
  setDocFiles,
  setFilterFiles,

  // Document operations
  setSingleDoc,
  removeUploadedDoc,
  setUpdatedDoc,

  // Tax refund form files
  addCustomerRefundFormFile,
  addCustomerRefundFormFileWithData,
  setAllCustomerRefundFormFiles,
  setCustomerRefundFormFile,
  setCustomerRefundFormFileID,
  removeCustomerRefundFormFile,

  // Loading states
  setFilesLoading,
  setFilesLoader,
  setAllFilesLoader,
  setAllFilesLoading,

  // Progress and error handling
  updateFileProgress,
  setFileError,

  // Individual document deletion loading states
  setDocumentDeleting,
  setDocumentsFromAPI,
  sanitizeLibraryFileById
} = customerFilesSlice.actions

// Selectors
export const selectCustomerFiles = state => state.customer_files
export const selectCustomerFilesList = state => state.customer_files.files // which is in documents Libaray
export const selectCustomerFilesLoading = state =>
  state.customer_files.filesLoading
export const selectCustomerFilesLoader = state =>
  state.customer_files.filesLoader
export const selectCustomerDocs = state => state.customer_files.docs
export const selectTaxRefundFormFiles = state =>
  state.customer_files.tax_refund_form_files
export const selectDeletingDocuments = state =>
  state.customer_files.deletingDocuments

export default customerFilesSlice.reducer
